import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthLayout } from "./AuthLayout";
import { useAuth } from "../context/AuthContext";
import { Loader2 } from "lucide-react";

export default function ForgotPassword() {
  const { forgotPassword } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [devToken, setDevToken] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await forgotPassword(email.trim());
    setLoading(false);
    if (res.ok) {
      setSent(true);
      if (res.data?.dev_reset_token) setDevToken(res.data.dev_reset_token);
    } else {
      setError(res.error);
    }
  };

  const goReset = () => {
    const params = new URLSearchParams();
    if (devToken) params.set("token", devToken);
    navigate(`/reset-password?${params.toString()}`);
  };

  return (
    <AuthLayout testid="forgot-page" quote="Forgot your password? We'll get you back in.">
      <div className="gt-label mb-3">Password reset</div>
      <h2 className="font-heading text-4xl font-black tracking-tighter mb-2">Forgot password</h2>
      <p className="text-[var(--gt-ink-2)] text-sm mb-8">
        Enter your account email and we&apos;ll issue a reset token.
      </p>

      {sent ? (
        <div className="gt-card" data-testid="forgot-success">
          <div className="font-heading font-bold text-lg mb-1">Check your email</div>
          <div className="text-sm text-[var(--gt-ink-2)]">
            If <span className="font-semibold text-[var(--gt-ink)]">{email}</span> matches an account, a reset link has been generated.
          </div>
          {devToken && (
            <div className="mt-4 text-xs bg-[var(--gt-bg-surface)] border border-[var(--gt-line)] p-3 rounded-sm">
              <div className="gt-label mb-1">Dev reset token</div>
              <div className="font-mono break-all" data-testid="dev-reset-token">{devToken}</div>
            </div>
          )}
          <div className="mt-6 flex gap-3">
            <button className="gt-btn-primary" onClick={goReset} data-testid="continue-to-reset-btn">
              Continue to reset
            </button>
            <Link to="/login" className="gt-btn-secondary" data-testid="back-to-login-btn">Back to login</Link>
          </div>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-5" data-testid="forgot-form">
          <div>
            <label className="gt-form-label" htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              className="gt-input"
              placeholder="you@yourvenue.com.au"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="forgot-email-input"
            />
          </div>

          {error && (
            <div className="text-sm text-[var(--gt-accent)] font-medium" data-testid="forgot-error">
              {error}
            </div>
          )}

          <button type="submit" className="gt-btn-primary" disabled={loading} data-testid="forgot-submit-btn">
            {loading && <Loader2 size={16} className="animate-spin" />}
            Send reset link
          </button>

          <div className="text-xs text-[var(--gt-ink-3)]">
            Remembered it?{" "}
            <Link to="/login" className="text-[var(--gt-ink)] font-semibold underline underline-offset-4">Back to login</Link>
          </div>
        </form>
      )}
    </AuthLayout>
  );
}
